import axios from 'axios';

export type OralExamPracticeAvailability = {
  available: boolean;
  courseId?: string | null;
  moduleId?: string | null;
  taskId?: string | null;
  sessionId?: string | null;
  bookingId?: string | null;
  slotId?: string | null;
  reason?: string | null;
};

export type OralExamPracticeEnsure = {
  courseId: string;
  moduleId: string;
  taskId: string;
  sessionId: string;
  /** Transition alias: equals sessionId for reservationless practice. */
  bookingId?: string | null;
  slotId?: string | null;
};

export const ORAL_EXAM_PRACTICE_KIND_ID = 'oral_exam_practice';

export function isOralExamPracticeTask(
  task?: { eventConfig?: { eventKindId?: string } | null } | null,
): boolean {
  return task?.eventConfig?.eventKindId === ORAL_EXAM_PRACTICE_KIND_ID;
}

export const oralExamPracticeQueryKey = (taskId: string) => ['oralExamPractice', taskId] as const;

export async function fetchOralExamPracticeAvailability(
  taskId: string,
): Promise<OralExamPracticeAvailability> {
  const response = await axios.get<{ data: OralExamPracticeAvailability }>(
    '/oral-exams/practice',
    { params: { taskId } },
  );
  const payload = response.data?.data;
  if (!payload) {
    return { available: false, reason: 'unavailable' };
  }
  return {
    available: Boolean(payload.available),
    courseId: payload.courseId ?? null,
    moduleId: payload.moduleId ?? null,
    taskId: payload.taskId ?? null,
    sessionId: payload.sessionId ?? null,
    bookingId: payload.bookingId ?? null,
    slotId: payload.slotId ?? null,
    reason: payload.reason ?? null,
  };
}

export async function ensureOralExamPractice(taskId: string): Promise<OralExamPracticeEnsure> {
  const response = await axios.post<{ data: OralExamPracticeEnsure }>(
    '/oral-exams/practice/ensure',
    { taskId },
  );
  const payload = response.data?.data;
  if (!payload?.sessionId) {
    throw new Error('Oral exam practice is not available');
  }
  return {
    ...payload,
    bookingId: payload.bookingId ?? payload.sessionId,
  };
}

export function startOralExamSelfServicePath(ids: {
  courseId: string;
  moduleId: string;
  taskId: string;
  bookingId: string;
}): string {
  return `/courses/${ids.courseId}/modules/${ids.moduleId}/tasks/${ids.taskId}/bookings/${ids.bookingId}/start-session`;
}

export function openOralExamAnswerWindowPath(ids: {
  courseId: string;
  moduleId: string;
  taskId: string;
  bookingId: string;
}): string {
  return `/courses/${ids.courseId}/modules/${ids.moduleId}/tasks/${ids.taskId}/bookings/${ids.bookingId}/answer-window`;
}
